import React, { useState } from 'react';
import { Wallet, WalletStats, PlayerFinanceStats, ReservedBalanceItem } from '../../../types';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/Card';
import { LoadingSpinner } from '../../ui/Loading';
import Button from '../../ui/Button';
import {
  Wallet as WalletIcon,
  AlertTriangle,
  Eye,
  X,
  ArrowDownCircle,
  ArrowUpCircle,
  DollarSign,
  TrendingUp,
  Clock,
  RefreshCw,
} from 'lucide-react';
import { formatCurrency, cn } from '../../../utils';
import { useFormatDate } from '../../../hooks/useFormatDate';
import { useReservedBalanceDetail } from '../../../hooks/useApi';
import WalletTransactionTable from './WalletTransactionTable';

interface WalletSectionProps {
  userId: string;
  wallet?: Wallet;
  stats?: WalletStats;
  financeStats?: PlayerFinanceStats;
  isLoading?: boolean;
  error?: any;
  accountTerminated?: boolean;
}

function FinanceStatCell({
  label,
  value,
  tone,
}: {
  label: string;
  value: number;
  tone?: 'positive' | 'negative' | 'neutral';
}) {
  const color =
    tone === 'positive'
      ? 'text-green-600'
      : tone === 'negative'
        ? 'text-red-600'
        : 'text-foreground';
  const signed = tone != null && value !== 0;
  return (
    <div>
      <div className="text-xs text-muted-foreground">{label}</div>
      <div className={cn('text-lg font-semibold tabular-nums', color)}>
        {signed && value > 0 ? '+' : ''}
        {formatCurrency(value)}
      </div>
    </div>
  );
}

const WalletSection: React.FC<WalletSectionProps> = ({
  userId,
  wallet,
  stats,
  financeStats,
  isLoading,
  error,
  accountTerminated,
}) => {
  const fmt = useFormatDate();
  const [showReservedDetail, setShowReservedDetail] = useState(false);

  const reservedBalance = wallet ? parseFloat(wallet.reservedBalance || '0') : 0;

  const {
    data: reservedDetailData,
    isLoading: reservedDetailLoading,
    error: reservedDetailError,
    refetch: refetchReservedDetail,
  } = useReservedBalanceDetail(userId, showReservedDetail);

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner />
      </div>
    );
  }

  if (error || !wallet) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <p className="text-destructive">Unable to load wallet information</p>
        </div>
      </div>
    );
  }

  const totalBalance = parseFloat(wallet.totalBalance || wallet.balance || '0');
  const availableBalance = parseFloat(wallet.availableBalance || '0');

  const reservedItems = reservedDetailData?.data?.items || [];

  const getItemTypeLabel = (item: ReservedBalanceItem) => {
    if (item.type === 'deposit' && item.paymentMethod === 'card') {
      return 'Card Reserve (pending payback)';
    }
    if (item.type === 'withdrawal') {
      return 'Pending Withdrawal';
    }
    if (item.type === 'deposit' && item.paymentMethod === 'wallet_transfer') {
      return 'Multi-game Deposit (pending)';
    }
    return `Pending ${item.type}`;
  };

  const getItemTypeIcon = (item: ReservedBalanceItem) => {
    if (item.type === 'deposit' && item.paymentMethod === 'card') {
      return <AlertTriangle className="h-4 w-4 text-amber-500" />;
    }
    if (item.type === 'withdrawal') {
      return <ArrowUpCircle className="h-4 w-4 text-orange-500" />;
    }
    return <ArrowDownCircle className="h-4 w-4 text-blue-500" />;
  };

  const pnlTone = (v: number): 'positive' | 'negative' | 'neutral' => {
    if (v > 0) return 'positive';
    if (v < 0) return 'negative';
    return 'neutral';
  };

  return (
    <div className="space-y-6">
      {accountTerminated ? (
        <div
          className="rounded-lg border border-stone-300 bg-stone-100 px-4 py-3 text-sm text-stone-800"
          role="status"
        >
          Transactions disabled — this account has been closed. Balances are shown for reconciliation only.
        </div>
      ) : null}
      {/* Wallet Balance Card */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center">
            <WalletIcon className="h-5 w-5 mr-2" />
            User Wallet
          </CardTitle>
        </CardHeader>
        <CardContent>
          {/* Balance Grid */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Total Balance */}
            <div className="p-4 rounded-lg bg-green-50 border border-green-200">
              <div className="flex items-center gap-2 mb-1">
                <DollarSign className="h-4 w-4 text-green-600" />
                <span className="text-sm font-medium text-green-700">Total Balance</span>
              </div>
              <div className="text-2xl font-bold text-green-700">
                {formatCurrency(totalBalance)} {wallet.currency}
              </div>
            </div>

            {/* Reserved Balance */}
            <div className={cn(
              "p-4 rounded-lg border",
              reservedBalance > 0
                ? "bg-amber-50 border-amber-200"
                : "bg-gray-50 border-gray-200"
            )}>
              <div className="flex items-center justify-between mb-1">
                <div className="flex items-center gap-2">
                  {reservedBalance > 0 && <AlertTriangle className="h-4 w-4 text-amber-600" />}
                  <span className={cn(
                    "text-sm font-medium",
                    reservedBalance > 0 ? "text-amber-700" : "text-gray-600"
                  )}>
                    Reserved Balance
                  </span>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  className={cn(
                    "h-7 px-2 text-xs",
                    reservedBalance > 0
                      ? "text-amber-700 hover:text-amber-900 hover:bg-amber-100"
                      : "text-gray-500 hover:text-gray-700 hover:bg-gray-100"
                  )}
                  onClick={() => setShowReservedDetail(!showReservedDetail)}
                >
                  <Eye className="h-3 w-3 mr-1" />
                  {showReservedDetail ? 'Hide' : 'View Detail'}
                </Button>
              </div>
              <div className={cn(
                "text-2xl font-bold",
                reservedBalance > 0 ? "text-amber-700" : "text-gray-500"
              )}>
                {formatCurrency(reservedBalance)} {wallet.currency}
              </div>
            </div>

            {/* Available Balance */}
            <div className="p-4 rounded-lg bg-blue-50 border border-blue-200">
              <div className="flex items-center gap-2 mb-1">
                <TrendingUp className="h-4 w-4 text-blue-600" />
                <span className="text-sm font-medium text-blue-700">Available Balance</span>
              </div>
              <div className="text-2xl font-bold text-blue-700">
                {formatCurrency(availableBalance)} {wallet.currency}
              </div>
            </div>
          </div>

          {financeStats && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">Player finance (realized)</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <FinanceStatCell label="Total deposit to wallet" value={financeStats.totalDepositToWallet} />
                <FinanceStatCell label="Total bonus credit (all sources)" value={financeStats.totalCredit} />
                <FinanceStatCell label="Total cash out (payout)" value={financeStats.totalCashOutFromWallet} />
                <FinanceStatCell
                  label="Wallet P&L"
                  value={financeStats.walletPnl}
                  tone={pnlTone(financeStats.walletPnl)}
                />
                <FinanceStatCell label="Master → game" value={financeStats.totalMasterToGame} />
                <FinanceStatCell label="Game → master" value={financeStats.totalGameToMaster} />
                <FinanceStatCell
                  label="Games P&L"
                  value={financeStats.gamesPnl}
                  tone={pnlTone(financeStats.gamesPnl)}
                />
              </div>
              <p className="mt-3 text-xs text-muted-foreground">
                Wallet balance = Wallet P&amp;L + Games P&amp;L + bonus credit.
                Wallet P&amp;L = current wallet balance − Games P&amp;L − bonus credit (all free credit sources).
                Games P&amp;L = game → master − master → game (realized transfers only).
              </p>
            </div>
          )}

          {stats && (
            <div className="mt-6 pt-6 border-t">
              <h4 className="text-sm font-semibold text-muted-foreground mb-3">Ledger activity</h4>
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                <div>
                  <div className="text-xs text-muted-foreground">Total Transactions</div>
                  <div className="text-lg font-semibold">{stats.totalTransactions}</div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Total Credits</div>
                  <div className="text-lg font-semibold text-green-600">
                    {formatCurrency(stats.totalCredits)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Total Debits</div>
                  <div className="text-lg font-semibold text-red-600">
                    {formatCurrency(stats.totalDebits)}
                  </div>
                </div>
                <div>
                  <div className="text-xs text-muted-foreground">Net Amount</div>
                  <div className={cn(
                    "text-lg font-semibold",
                    stats.netAmount >= 0 ? "text-green-600" : "text-red-600"
                  )}>
                    {stats.netAmount >= 0 ? '+' : ''}{formatCurrency(stats.netAmount)}
                  </div>
                </div>
              </div>
              {stats.lastTransactionDate && (
                <div className="mt-2 text-xs text-muted-foreground flex items-center gap-1">
                  <Clock className="h-3 w-3" />
                  Last transaction: {fmt(stats.lastTransactionDate)}
                </div>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      {/* Reserved Balance Detail */}
      {showReservedDetail && (
        <Card className={cn("border", reservedBalance > 0 ? "border-amber-200" : "border-gray-200")}>
          <CardHeader>
            <div className="flex items-center justify-between">
              <CardTitle className="flex items-center text-amber-800">
                <AlertTriangle className="h-5 w-5 mr-2" />
                Reserved Balance Detail
              </CardTitle>
              <div className="flex items-center gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => refetchReservedDetail()}
                  disabled={reservedDetailLoading}
                >
                  <RefreshCw className={cn("h-4 w-4 mr-1", reservedDetailLoading && "animate-spin")} />
                  Refresh
                </Button>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => setShowReservedDetail(false)}
                >
                  <X className="h-4 w-4" />
                </Button>
              </div>
            </div>
          </CardHeader>
          <CardContent>
            {reservedDetailLoading ? (
              <div className="flex justify-center items-center h-32">
                <LoadingSpinner />
              </div>
            ) : reservedDetailError ? (
              <div className="text-center text-destructive py-4">
                Unable to load reserved balance detail.
                <button
                  onClick={() => refetchReservedDetail()}
                  className="ml-2 text-sm text-primary hover:underline"
                >
                  Try again
                </button>
              </div>
            ) : (
              <>
                {/* Summary */}
                <div className="flex flex-wrap gap-6 mb-4 text-sm">
                  <div>
                    <span className="text-muted-foreground">Reserved:</span>{' '}
                    <span className="font-semibold text-amber-700">
                      {formatCurrency(reservedDetailData?.data?.reservedBalance || 0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Total:</span>{' '}
                    <span className="font-semibold">
                      {formatCurrency(reservedDetailData?.data?.totalBalance || 0)}
                    </span>
                  </div>
                  <div>
                    <span className="text-muted-foreground">Available:</span>{' '}
                    <span className="font-semibold text-blue-700">
                      {formatCurrency(reservedDetailData?.data?.availableBalance || 0)}
                    </span>
                  </div>
                </div>

                {/* Stale warning */}
                {reservedBalance > 0 && reservedItems.length === 0 && !reservedDetailLoading && (
                  <div className="flex items-center gap-2 p-3 rounded-lg bg-red-50 border border-red-200 text-red-700 text-sm mb-4">
                    <AlertTriangle className="h-4 w-4 shrink-0" />
                    <span>
                      Reserved balance is greater than 0 but no pending transactions found. 
                      This may indicate a stale/orphan reserved balance that needs manual investigation.
                    </span>
                  </div>
                )}

                {/* Pending Transactions Table */}
                {reservedItems.length > 0 && (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b">
                          <th className="text-right p-3 font-medium text-sm">Amount</th>
                          <th className="text-left p-3 font-medium text-sm">Transaction ID</th>
                          <th className="text-left p-3 font-medium text-sm">Status</th>
                          <th className="text-left p-3 font-medium text-sm">Created</th>
                        </tr>
                      </thead>
                      <tbody>
                        {reservedItems.map((item) => (
                          <tr key={item.transactionId} className="border-b hover:bg-accent/50">
                            <td className="p-3 text-sm text-right font-medium text-amber-700">
                              {formatCurrency(item.amount)} {item.currency}
                            </td>
                            <td className="p-3 text-sm">
                              <div className="flex items-center gap-2">
                                {getItemTypeIcon(item)}
                                <span className="text-xs text-muted-foreground">{getItemTypeLabel(item)}</span>
                              </div>
                              <span className="font-mono text-xs">{item.transactionId}</span>
                              {item.linkedPaymentTransactionId && (
                                <div className="text-xs text-muted-foreground mt-0.5">
                                  Linked: {item.linkedPaymentTransactionId}
                                </div>
                              )}
                            </td>
                            <td className="p-3 text-sm">
                              <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-yellow-100 text-yellow-800">
                                {item.status}
                              </span>
                            </td>
                            <td className="p-3 text-sm">
                              {fmt(item.createdAt)}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      )}

      {/* Wallet Transactions Table */}
      <WalletTransactionTable userId={userId} wallet={wallet} />
    </div>
  );
};

export default WalletSection;
